import { type FC, useCallback, useEffect, useState } from "react";

import { providerRowLabel } from "../../constants/embeddingReadiness";
import {
  MULTI_FIELD_PROVIDER_KEYS,
  PROVIDERS,
  PROVIDER_LABELS,
} from "../../constants/providers";
import {
  type DashboardKeystoreEntry,
  type DashboardOrganizationApiKey,
  assignKeystoreEntryAsAdmin,
  listKeystoreEntriesAsAdmin,
  unassignKeystoreEntryAsAdmin,
} from "../../dashboard-client";
import { useApiKeys } from "../../hooks/useApiKeys";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmbeddingReadinessNotice } from "../common/EmbeddingReadinessNotice";
import { LoadingState } from "../common/LoadingState";
import { ProviderKeyRow } from "../common/ProviderKeyRow";
import { Text } from "../common/Text";

/**
 * Inline API key management panel for a single organization.
 * Rendered inside the expanded org row in the admin organizations table.
 */
export const AdminOrgApiKeys: FC<{ orgId: string }> = ({ orgId }) => {
  const { keys, loading, error, refresh } = useApiKeys(orgId);

  if (loading) {
    return <LoadingState message="Loading API keys..." />;
  }
  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  const bedrockConfigs = keys.filter((k) => k.provider === "bedrock");

  return (
    <div>
      <Text as="p" className="mb-2">
        Provider keys (Anthropic, Azure AI Foundry, Bedrock, Google Generative
        AI, OpenAI, OpenRouter, Vertex AI) for AI workflows — install the
        providers you use. Bedrock supports multiple entries (one per model).
      </Text>
      <EmbeddingReadinessNotice className="mb-2" keys={keys} />
      {PROVIDERS.map((provider) => {
        if (provider === "bedrock") {
          return (
            <div key="bedrock">
              {bedrockConfigs.length === 0 ? (
                <AdminProviderKeyRow
                  provider="bedrock"
                  orgId={orgId}
                  config={undefined}
                  onRefresh={() => refresh()}
                />
              ) : (
                bedrockConfigs.map((config) => (
                  <AdminProviderKeyRow
                    key={`bedrock-${config.provider_key_id ?? "default"}`}
                    provider="bedrock"
                    providerKeyId={config.provider_key_id ?? "default"}
                    orgId={orgId}
                    config={config}
                    onRefresh={() => refresh()}
                  />
                ))
              )}
              {/* Always show an "Add another bedrock" option */}
              {bedrockConfigs.length > 0 && (
                <AdminProviderKeyRow
                  provider="bedrock"
                  orgId={orgId}
                  config={undefined}
                  onRefresh={() => refresh()}
                />
              )}
            </div>
          );
        }
        const keyFields = MULTI_FIELD_PROVIDER_KEYS[provider];
        if (keyFields) {
          return (
            <div key={provider}>
              {keyFields.map((field) => (
                <AdminProviderKeyRow
                  key={`${provider}-${field.value}`}
                  provider={provider}
                  providerKeyId={field.value}
                  label={`${PROVIDER_LABELS[provider] ?? provider} — ${field.label}`}
                  orgId={orgId}
                  config={findProviderKeyConfig(keys, provider, field.value)}
                  onRefresh={() => refresh()}
                />
              ))}
            </div>
          );
        }
        return (
          <AdminProviderKeyRow
            key={provider}
            provider={provider}
            label={providerRowLabel(provider)}
            orgId={orgId}
            config={findProviderKeyConfig(keys, provider)}
            onRefresh={() => refresh()}
          />
        );
      })}
    </div>
  );
};

/** Wraps ProviderKeyRow with admin-only keystore assign/unassign functionality. */
const AdminProviderKeyRow: FC<{
  provider: string;
  providerKeyId?: string;
  label?: string;
  orgId: string;
  config: DashboardOrganizationApiKey | undefined;
  onRefresh: () => Promise<void>;
}> = ({ provider, providerKeyId, label, orgId, config, onRefresh }) => {
  const [showKeystorePicker, setShowKeystorePicker] = useState(false);
  const [keystoreEntries, setKeystoreEntries] = useState<
    DashboardKeystoreEntry[]
  >([]);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [keystoreLoading, setKeystoreLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [keystoreError, setKeystoreError] = useState<string | null>(null);

  const isConfigured = config?.has_key === true;

  const loadKeystoreEntries = useCallback(async () => {
    setKeystoreLoading(true);
    try {
      const { data } = await listKeystoreEntriesAsAdmin({
        query: { status: "available" },
      });
      setKeystoreEntries(
        data.entries.filter(
          (entry) =>
            entry.provider === provider &&
            (providerKeyId === undefined ||
              providerKeyIdMatches(entry.provider_key_id, providerKeyId)),
        ),
      );
    } catch {
      setKeystoreEntries([]);
    } finally {
      setKeystoreLoading(false);
    }
  }, [provider, providerKeyId]);

  useEffect(() => {
    if (showKeystorePicker) {
      void loadKeystoreEntries();
    }
  }, [showKeystorePicker, loadKeystoreEntries]);

  const handleKeystoreAssign = useCallback(async () => {
    if (!selectedEntryId) {
      return;
    }
    setKeystoreError(null);
    setActionLoading("keystore-assign");
    try {
      await assignKeystoreEntryAsAdmin({
        body: { organizationId: orgId },
        path: { entryId: selectedEntryId },
      });
      setShowKeystorePicker(false);
      setSelectedEntryId(null);
      await onRefresh();
    } catch (error) {
      setKeystoreError(
        error instanceof Error
          ? error.message
          : "Failed to assign from keystore",
      );
    } finally {
      setActionLoading(null);
    }
  }, [selectedEntryId, orgId, onRefresh]);

  // Find the keystore entry assigned to this org+provider so we can unassign it
  const [assignedEntryId, setAssignedEntryId] = useState<string | null>(null);

  const loadAssignedEntry = useCallback(async () => {
    if (!isConfigured) {
      setAssignedEntryId(null);
      return;
    }
    try {
      const { data } = await listKeystoreEntriesAsAdmin({
        query: { status: "assigned" },
      });
      const match = data.entries
        .filter((entry) => entry.provider === provider)
        .find(
          (entry) =>
            entry.assigned_org_id === orgId &&
            (providerKeyId === undefined ||
              providerKeyIdMatches(entry.provider_key_id, providerKeyId)),
        );
      setAssignedEntryId(match?.entry_id ?? null);
    } catch {
      setAssignedEntryId(null);
    }
  }, [provider, providerKeyId, orgId, isConfigured]);

  useEffect(() => {
    void loadAssignedEntry();
  }, [loadAssignedEntry]);

  const handleUnassign = useCallback(async () => {
    if (!assignedEntryId) {
      return;
    }
    setKeystoreError(null);
    setActionLoading("unassign");
    try {
      await unassignKeystoreEntryAsAdmin({
        path: { entryId: assignedEntryId },
      });
      setAssignedEntryId(null);
      await onRefresh();
    } catch (error) {
      setKeystoreError(
        error instanceof Error ? error.message : "Failed to unassign key",
      );
    } finally {
      setActionLoading(null);
    }
  }, [assignedEntryId, onRefresh]);

  const extraActions = (
    <>
      {!isConfigured && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setShowKeystorePicker(!showKeystorePicker);
            setKeystoreError(null);
          }}
        >
          Assign from Keystore
        </Button>
      )}
      {isConfigured && assignedEntryId && (
        <Button
          variant="danger"
          size="sm"
          onClick={() => void handleUnassign()}
          loading={actionLoading === "unassign"}
          loadingText="Unassigning..."
        >
          Unassign
        </Button>
      )}
    </>
  );

  const extraPanels =
    showKeystorePicker && !isConfigured ? (
      <div className="mt-3 rounded-none border border-slate-300 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/40">
        {keystoreError && (
          <Alert variant="error" size="sm" className="mb-2">
            {keystoreError}
          </Alert>
        )}
        {keystoreLoading && <Text>Loading available keys...</Text>}
        {!keystoreLoading && keystoreEntries.length === 0 && (
          <Text>
            No available keystore entries for{" "}
            {PROVIDER_LABELS[provider] ?? provider}.
          </Text>
        )}
        {!keystoreLoading && keystoreEntries.length > 0 && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Text variant="label" as="label" className="mb-1 block">
                Available Keys
              </Text>
              <select
                className="focus:border-emerald-500 focus:ring-emerald-500/30 w-full rounded-none border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:outline-none focus:ring-1 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                value={selectedEntryId ?? ""}
                onChange={(e) => setSelectedEntryId(e.target.value || null)}
              >
                <option value="">Select a key...</option>
                {keystoreEntries.map((entry) => (
                  <option key={entry.entry_id} value={entry.entry_id}>
                    {entry.label ?? `Key ${entry.entry_id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleKeystoreAssign()}
              loading={actionLoading === "keystore-assign"}
              loadingText="Assigning..."
              disabled={!selectedEntryId}
            >
              Assign
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowKeystorePicker(false);
                setSelectedEntryId(null);
                setKeystoreError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    ) : undefined;

  const rowLabel =
    label ??
    (provider === "bedrock" && providerKeyId && providerKeyId !== "default"
      ? `${PROVIDER_LABELS[provider] ?? provider} — ${providerKeyId}`
      : undefined);

  return (
    <ProviderKeyRow
      provider={provider}
      label={rowLabel}
      config={config}
      extraActions={extraActions}
      extraPanels={extraPanels}
    />
  );
};

function findProviderKeyConfig(
  keys: DashboardOrganizationApiKey[],
  provider: string,
  providerKeyId?: string,
) {
  return keys.find(
    (key) =>
      key.provider === provider &&
      (providerKeyId === undefined ||
        providerKeyIdMatches(key.provider_key_id, providerKeyId)),
  );
}

function providerKeyIdMatches(
  actualProviderKeyId: string | null,
  expectedProviderKeyId: string,
): boolean {
  const actual = actualProviderKeyId ?? "default";
  if (expectedProviderKeyId === "apiKey") {
    return actual === "apiKey" || actual === "default";
  }
  return actual === expectedProviderKeyId;
}
